/**
 * THE ONE TABLE that turns a tool id into words a person reads.
 *
 * `dashboard.plan`, `analytics.query_metric`, `workforce.list_workers` are wire
 * identifiers. They are the right names for a contract and the wrong names for
 * a sentence, so no id ever reaches a reader: everything the surface says about
 * where an answer came from goes through this file.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THIS TABLE IS SMALLER THAN IT WAS.
 *
 * It used to carry three tenses per tool — `doing` ("Checking the workforce
 * roster…"), `done` ("Checked the workforce roster") and `attempt` — because
 * the surface narrated a finished answer step by step: "Listed what can be
 * measured", "Worked out the numbers", "Drafted a dashboard for you to
 * confirm". That trail is gone. A finished answer shows the RESULT; the
 * sequence of tool calls that produced it is the runtime's business, and
 * replaying it taught readers to audit an implementation instead of reading an
 * answer.
 *
 * The tenses are deleted rather than left unused on purpose. A `done` phrase
 * sitting in this table is a standing invitation to render a trail again, and
 * the cheapest way to keep narration out of the product is to keep the words
 * for it out of the codebase.
 *
 * WHAT SURVIVES is the one phrasing the product still needs: the AREA an
 * answer drew on, for the "Based on Phibian records" provenance lines. It is a
 * noun phrase, not a verb, because provenance names a source rather than an
 * action — "Workforce records", never "Checked the workforce roster".
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THERE IS NO FAMILY-PREFIX FALLBACK. `workforce.*` → "Workforce records"
 * looks like a kinder degradation than the neutral phrase and is a worse one:
 * the next tool registered in a family may read something the family name does
 * not cover, and a guessed source on a provenance line is a false citation. A
 * phrase that admits ignorance cannot be wrong; a guessed one can.
 */

/** The area an answer drew on, as a noun phrase: "Workforce records". */
export interface ToolPhrase {
  source: string;
}

/**
 * Tool id → the area it reads. Every id here is one the API registers today
 * (`domains/intelligence/tools/*` plus `automation/tools.ts`), and the unit
 * suite asserts that list stays covered, so a newly registered tool is a test
 * failure rather than a silent neutral phrase in production.
 */
export const TOOL_PHRASES: Readonly<Record<string, ToolPhrase>> = {
  // --- retrieval -----------------------------------------------------------
  'workforce.list_workers': { source: 'Workforce records' },
  'supervision.status': { source: 'Supervision records' },
  'supervision.overview': { source: 'Supervision records' },
  'connectors.health': { source: 'Integration health' },
  // --- knowledge -----------------------------------------------------------
  'knowledge.list': { source: 'Policies and guidance' },
  'knowledge.get': { source: 'Policies and guidance' },
  // --- analytics -----------------------------------------------------------
  // Superseded on a provenance line by the metric's own label when the turn
  // carried an analytics result ("Hours worked"); this is what shows when it
  // did not.
  'analytics.query_metric': { source: 'Your organization’s records' },
  // --- dashboards ----------------------------------------------------------
  'dashboard.list_metrics': { source: 'What your organization measures' },
  'dashboard.explain_metric': { source: 'What your organization measures' },
  'dashboard.explain_spec': { source: 'Your dashboards' },
  'dashboard.plan': { source: 'Your dashboards' },
  'dashboard.create': { source: 'Your dashboards' },
  'dashboard.update': { source: 'Your dashboards' },
  'dashboard.duplicate': { source: 'Your dashboards' },
  'dashboard.propose_automation': { source: 'Your alerts' },
  // --- automation ----------------------------------------------------------
  'automation.plan': { source: 'Your automations' },
  'automation.create': { source: 'Your automations' },
};

/**
 * The honest degradation for an id this build has no words for.
 *
 * It names the only thing that is certainly true — the answer drew on this
 * organization's records — and claims nothing about which ones.
 */
export const UNNAMED_TOOL: ToolPhrase = Object.freeze({ source: 'Phibian records' });

/**
 * The id-leak guard, applied to every phrase on the way out.
 *
 * The failure this prevents is not hypothetical: the natural "helpful" edit to
 * a table like this is `?? { source: toolId }`, and it ships a wire identifier
 * onto a provenance line on a screen whose whole promise is plain words. Rather
 * than trust review to catch that forever, every exit from this module runs
 * through here, so a phrase carrying its own id becomes the neutral phrase
 * instead of reaching a reader.
 *
 * Case-insensitive and separator-insensitive on purpose: `Workforce.List_Workers`
 * and `workforce_list_workers` are the same leak wearing a hat.
 */
function looksLikeId(text: string, toolId: string): boolean {
  const flatten = (value: string): string => value.toLowerCase().replace(/[._\-\s]/g, '');
  return flatten(text).includes(flatten(toolId));
}

function guard(phrase: ToolPhrase, toolId: string): ToolPhrase {
  return looksLikeId(phrase.source, toolId) ? UNNAMED_TOOL : phrase;
}

/**
 * The words for one tool id. Total: every id has a phrase, and no phrase is
 * ever the id.
 */
export function phraseForTool(toolId: string): ToolPhrase {
  const known = Object.prototype.hasOwnProperty.call(TOOL_PHRASES, toolId)
    ? TOOL_PHRASES[toolId]
    : undefined;
  if (known === undefined) return UNNAMED_TOOL;
  return guard(known, toolId);
}

/** True when this build has words of its own for `toolId`. */
export function isNamedTool(toolId: string): boolean {
  return phraseForTool(toolId) !== UNNAMED_TOOL;
}

/** The area an answer drew on, for a provenance line. Never a tool id. */
export function sourcePhrase(toolId: string): string {
  return phraseForTool(toolId).source;
}
